import React, { useCallback, useMemo, useState } from 'react'
import {
    BillType,
    FullBillFragment,
    FullPopularBillFragment,
    MerchantSummaryFragment,
    useBillsDataQuery,
    useMerchantsQuery,
    usePopularBillsQuery,
} from '../../../../graphql/generated/graphql'
import { LinkBillContent } from './LinkBillContent'
import { BillSelect, LinkBill as LinkBillType } from './types'

export type MerchantDetails = MerchantSummaryFragment & { account?: string }
interface ILinkBillStepContext {
    step: LinkBillType
    setStep: React.Dispatch<React.SetStateAction<LinkBillType>>
    bills: FullBillFragment[]
    popularBills: FullPopularBillFragment[]
    loading: boolean
    showPopularBills: boolean
    addBillOfType: (billType: BillSelect) => void
    selectBillType: BillSelect | null
    setSelectBillType: React.Dispatch<React.SetStateAction<BillSelect | null>>
    merchant: MerchantDetails | null
    addAccountForMerchant: (merchant: MerchantDetails) => void
    onLinkBill: (billId: string) => void
    onCantPayBill: () => void
}

export const LinkBillContext = React.createContext<ILinkBillStepContext>({} as ILinkBillStepContext)

export const useLinkBillContext = () => {
    return React.useContext(LinkBillContext)
}

const LinkBillProvider: React.FC<{
    onLinkBill: (billId: string) => void
    onCantPayBill: () => void
    showConnected: boolean
    showPopularBills: boolean
}> = ({ children, onLinkBill, onCantPayBill, showConnected, showPopularBills }) => {
    const { data: popularBillsData, loading } = usePopularBillsQuery({ fetchPolicy: 'cache-first' })
    const { data } = useBillsDataQuery()
    useMerchantsQuery()
    const [step, setStep] = useState(LinkBillType.SelectBillType)
    const [selectBillType, setSelectBillType] = useState<BillSelect | null>(null)
    const [merchant, setMerchant] = useState<MerchantDetails | null>(null)
    const addAccountForMerchant = (merchant: MerchantDetails) => {
        setMerchant(merchant)
        setStep(LinkBillType.LinkAccount)
    }

    const allBills = useMemo(() => data?.bills ?? [], [data?.bills])
    const bills = showConnected ? allBills : allBills.filter((bill) => !bill.connected)
    const popularBills = useMemo(
        () => popularBillsData?.popularBills ?? [],
        [popularBillsData?.popularBills]
    )

    const addBillOfType = useCallback(
        (billType: BillSelect) => {
            setSelectBillType(billType)
            const hasBillOfType = bills.some(
                (bill) => bill.type === (billType?.type as unknown as BillType)
            )
            if (hasBillOfType) {
                setStep(LinkBillType.AddExistingBill)
            } else {
                setStep(LinkBillType.AddNewBill)
            }
        },
        [bills]
    )

    const contextValue = useMemo(
        () => ({
            bills,
            step,
            setStep,
            selectBillType,
            setSelectBillType,
            addBillOfType,
            merchant,
            addAccountForMerchant,
            onLinkBill,
            onCantPayBill,
            popularBills,
            showPopularBills,
            loading,
        }),
        [
            bills,
            step,
            selectBillType,
            setSelectBillType,
            addBillOfType,
            merchant,
            onLinkBill,
            onCantPayBill,
            popularBills,
            showPopularBills,
            loading,
        ]
    )

    return <LinkBillContext.Provider value={contextValue}>{children}</LinkBillContext.Provider>
}

export const LinkBill = ({
    onLinkBill,
    onCantPayBill,
    showConnected = false,
    popularBills = false,
    selectBillHeader,
}: {
    onLinkBill: (billId: string) => void
    onCantPayBill: () => void
    showConnected?: boolean
    popularBills?: boolean
    selectBillHeader?: (foundBills: boolean, billCount?: string) => React.ReactNode
}) => {
    return (
        <LinkBillProvider
            onLinkBill={onLinkBill}
            onCantPayBill={onCantPayBill}
            showConnected={showConnected}
            showPopularBills={popularBills}
        >
            <LinkBillContent selectBillHeader={selectBillHeader} />
        </LinkBillProvider>
    )
}
